import { useEffect, useState } from 'react';
import { FileSpreadsheet, FileText, RefreshCw, Plus, Ban, HelpCircle, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { libroCajaApi, ventasApi, comprasRepuestosApi, creditosPasadosApi, pagosCreditoApi } from '../lib/api';
import FilaLibroModal from './FilaLibroModal';
import { cerrarAlTocarFondo } from '../lib/tocarFondo';

// Libro de caja de Caja Repuestos, con el mismo formato del Excel "REPUESTO" de Rogelio:
// Fecha | Descripcion | Entrada | Salida | Saldo. Desde 2026-10-20 tambien se digita aca
// ("+ Agregar fila", ver FilaLibroModal) y se anula por fila, siempre via las rutas de siempre
// (venta al contado, gasto, compra) para que stock, deudas y cajas sigan cuadrando.
interface Fila {
  id: string;
  fecha: string;
  tipo: 'ingreso' | 'gasto' | 'saldo_inicial';
  descripcion: string;
  metodo_pago?: string | null;
  entrada: number | null;
  salida: number | null;
  saldo: number;
  origen?: string;
  venta_id?: string | null;
  compra_id?: string | null;
  pago_bus_id?: string | null;
  anulable?: boolean;
  es_yape?: boolean;
  precio_cambiado?: PrecioCambiado | null;
}
interface PrecioCambiado {
  cambios: { producto: string; precio_lista: number; precio_cobrado: number; cantidad: number }[];
  descuento: number;
  motivo: string;
  usuario: string;
  fecha: string;
}
interface Libro {
  desde: string;
  hasta: string;
  saldo_anterior: number;
  total_entradas: number;
  total_salidas: number;
  saldo_final: number;
  total_yape_entradas?: number;
  total_yape_salidas?: number;
  filas: Fila[];
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmt = (n: number) => n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fechaCorta = (s: string) => { const d = new Date(s); return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
const fechaTexto = (s: string) => s.split('-').reverse().join('/');

const rangoRapido = (r: 'hoy' | 'semana' | 'mes') => {
  const hoy = new Date();
  if (r === 'hoy') return [iso(hoy), iso(hoy)];
  if (r === 'semana') {
    const lunes = new Date(hoy); lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7));
    return [iso(lunes), iso(hoy)];
  }
  return [iso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), iso(hoy)];
};

const LibroCaja = ({ onCambio }: { onCambio?: () => void }) => {
  const [desde, setDesde] = useState(rangoRapido('hoy')[0]);
  const [hasta, setHasta] = useState(rangoRapido('hoy')[1]);
  const [libro, setLibro] = useState<Libro | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [mostrarAgregar, setMostrarAgregar] = useState(false);
  const [anulando, setAnulando] = useState<Fila | null>(null);
  const [clave, setClave] = useState('');
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [verPrecio, setVerPrecio] = useState<Fila | null>(null);

  const cargar = async (d = desde, h = hasta) => {
    setCargando(true); setError('');
    try {
      setLibro(await libroCajaApi.get(d, h));
    } catch (e: any) {
      setError(e.message || 'No se pudo cargar el libro de caja');
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Tras agregar o anular: recarga el libro hasta hoy (para ver la fila nueva) y avisa a la caja
  const refrescar = () => {
    const h = rangoRapido('hoy')[1];
    const d = desde > h ? h : desde;
    setDesde(d); setHasta(h); cargar(d, h); onCambio?.();
  };

  const confirmarAnular = async () => {
    if (!anulando) return;
    if (!clave) return toast.error('Ingresa la clave de administrador');
    if (!motivo.trim()) return toast.error('Ingresa el motivo');
    setProcesando(true);
    try {
      const datos = { clave, motivo: motivo.trim() };
      if (anulando.origen === 'venta' && anulando.venta_id) await ventasApi.anular(anulando.venta_id, datos);
      else if (anulando.origen === 'compra' && anulando.compra_id) await comprasRepuestosApi.anular(anulando.compra_id, datos);
      else if (anulando.origen === 'gasto') await libroCajaApi.anularGasto(anulando.id, datos);
      else if (anulando.origen === 'pago_deuda_antigua') {
        const r = await creditosPasadosApi.anularPago(anulando.id, datos);
        const otra = r.cajas.find(c => c.caja !== 'ventas');
        if (otra) toast.info(`Ese pago también tenía S/ ${fmt(otra.monto)} en Caja Servicios: se anuló completo`);
      }
      else if (anulando.origen === 'pago_deuda' && anulando.pago_bus_id) await pagosCreditoApi.anular(anulando.pago_bus_id, datos);
      else throw new Error('Esta fila no se puede anular desde aquí');
      toast.success('Fila anulada');
      setAnulando(null);
      refrescar();
    } catch (e: any) {
      toast.error(e.message || 'No se pudo anular');
    } finally {
      setProcesando(false);
    }
  };

  const elegir = (r: 'hoy' | 'semana' | 'mes') => {
    const [d, h] = rangoRapido(r); setDesde(d); setHasta(h); cargar(d, h);
  };

  const titulo = libro ? (libro.desde === libro.hasta ? `Día ${fechaTexto(libro.desde)}` : `Del ${fechaTexto(libro.desde)} al ${fechaTexto(libro.hasta)}`) : '';
  // Desde 2026-10-28 el libro cuenta solo EFECTIVO: lo de Yape se ve (en gris, entre paréntesis) pero no suma al saldo
  const esYape = (f: Fila) => !!f.es_yape;
  const yape = (f: Fila) => (esYape(f) ? ' (YAPE - no suma al efectivo)' : '');
  const textoPrecio = (p: PrecioCambiado) => [
    ...p.cambios.map(c => `${c.producto}: lista S/ ${fmt(Number(c.precio_lista) || 0)}, cobrado S/ ${fmt(Number(c.precio_cobrado) || 0)}`),
    ...(p.descuento > 0 ? [`Descuento S/ ${fmt(p.descuento)}`] : []),
  ].join('; ') + (p.motivo ? `. Motivo: ${p.motivo}` : '') + (p.usuario ? ` (${p.usuario})` : '');
  const montoExport = (f: Fila, m: number | null) => (m == null ? '' : esYape(f) ? `(${fmt(m)})` : m);
  const totalYape = (libro?.total_yape_entradas || 0) - (libro?.total_yape_salidas || 0);

  const filasExport = () => {
    if (!libro) return [] as (string | number)[][];
    return [
      [fechaTexto(libro.desde), 'SALDO ANTERIOR', '', '', libro.saldo_anterior],
      ...libro.filas.flatMap(f => {
        const fila: (string | number)[] = [fechaCorta(f.fecha), f.descripcion + yape(f), montoExport(f, f.entrada), montoExport(f, f.salida), f.saldo];
        return f.precio_cambiado ? [fila, ['', '   PRECIO CAMBIADO: ' + textoPrecio(f.precio_cambiado), '', '', '']] : [fila];
      }),
      ['', 'TOTALES (solo efectivo)', libro.total_entradas, libro.total_salidas, libro.saldo_final],
      ...(totalYape || libro.total_yape_entradas ? [['', 'Cobrado por Yape en el periodo (no está en la caja)', libro.total_yape_entradas || 0, libro.total_yape_salidas || 0, '']] : []),
    ];
  };

  const exportarExcel = () => {
    if (!libro) return;
    const aoa = [['REPUESTO — ' + titulo], [], ['FECHA', 'DESCRIPCIÓN', 'ENTRADA', 'SALIDA', 'SALDO'], ...filasExport()];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 60 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Repuesto');
    XLSX.writeFile(wb, `Libro_Caja_Repuestos_${libro.desde}_${libro.hasta}.xlsx`);
  };

  const exportarPdf = () => {
    if (!libro) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    doc.setFontSize(14); doc.setTextColor(47, 84, 150); doc.text('REPUESTO — Libro de Caja Repuestos', 14, 14);
    doc.setFontSize(9); doc.setTextColor(0); doc.text(titulo, 14, 20);
    const body = filasExport().map(r => r.map((c, i) => (i >= 2 && typeof c === 'number' ? fmt(c) : String(c))));
    autoTable(doc, {
      startY: 24, head: [['Fecha', 'Descripción', 'Entrada', 'Salida', 'Saldo']], body,
      styles: { fontSize: 8, cellPadding: 1.5 }, headStyles: { fillColor: [189, 215, 238], textColor: 0 },
      alternateRowStyles: { fillColor: [221, 235, 247] },
      columnStyles: { 0: { cellWidth: 20 }, 2: { halign: 'right', cellWidth: 22 }, 3: { halign: 'right', cellWidth: 22 }, 4: { halign: 'right', cellWidth: 24 } },
    });
    doc.save(`Libro_Caja_Repuestos_${libro.desde}_${libro.hasta}.pdf`);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6">
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="flex gap-1">
          <button onClick={() => elegir('hoy')} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">Hoy</button>
          <button onClick={() => elegir('semana')} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">Semana</button>
          <button onClick={() => elegir('mes')} className="px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">Mes</button>
        </div>
        <label className="text-sm">Desde <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="border rounded px-2 py-1.5 ml-1" /></label>
        <label className="text-sm">Hasta <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="border rounded px-2 py-1.5 ml-1" /></label>
        <button onClick={() => cargar()} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
          <RefreshCw size={14} /> Ver
        </button>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => setMostrarAgregar(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold">
            <Plus size={14} /> Agregar fila
          </button>
          <button onClick={exportarExcel} disabled={!libro} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50">
            <FileSpreadsheet size={14} /> Excel
          </button>
          <Link to="/ayuda" title="Manual del Libro de caja" className="flex items-center gap-1 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-semibold text-blue-700">
            <HelpCircle size={14} /> Ayuda
          </Link>
          <button onClick={exportarPdf} disabled={!libro} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50">
            <FileText size={14} /> PDF
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      {cargando && !libro ? (
        <p className="text-center py-8 text-gray-500">Cargando...</p>
      ) : libro && (
        <div className="overflow-x-auto border border-blue-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr><th colSpan={6} className="bg-blue-100 text-blue-700 text-2xl font-bold py-2 text-center tracking-wide">REPUESTO</th></tr>
              <tr className="bg-white border-b-2 border-blue-300 text-xs font-bold text-gray-800">
                <th className="px-2 py-2 text-left w-24">FECHA</th>
                <th className="px-2 py-2 text-left">DESCRIPCIÓN</th>
                <th className="px-2 py-2 text-right w-24">ENTRADA</th>
                <th className="px-2 py-2 text-right w-24">SALIDA</th>
                <th className="px-2 py-2 text-right w-28">SALDO</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-blue-50 border-b border-blue-200 font-semibold">
                <td className="px-2 py-1.5">{fechaTexto(libro.desde)}</td>
                <td className="px-2 py-1.5">SALDO ANTERIOR</td>
                <td></td><td></td>
                <td className="px-2 py-1.5 text-right">{fmt(libro.saldo_anterior)}</td>
                <td></td>
              </tr>
              {libro.filas.length === 0 && (
                <tr><td colSpan={6} className="text-center py-6 text-gray-500">Sin movimientos en estas fechas</td></tr>
              )}
              {libro.filas.map((f, i) => (
                <tr key={f.id} className={`border-b border-blue-100 ${i % 2 ? 'bg-blue-50' : 'bg-white'} ${f.tipo === 'saldo_inicial' ? 'font-semibold' : ''} ${esYape(f) ? 'text-gray-400' : ''}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fechaCorta(f.fecha)}</td>
                  <td className="px-2 py-1.5">
                    {f.descripcion}
                    {esYape(f) && <span title="Pagado por Yape: no suma al efectivo de la caja" className="ml-1 px-1.5 rounded bg-purple-100 text-purple-800 text-xs font-semibold">YAPE · no suma</span>}
                    {f.precio_cambiado && (
                      <button onClick={() => setVerPrecio(f)} title="Ver por qué se cambió el precio"
                        className="ml-1 inline-flex items-center gap-0.5 px-1.5 rounded bg-orange-100 text-orange-800 text-xs font-semibold hover:bg-orange-200">
                        <Tag size={11} /> PRECIO CAMBIADO
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">{f.entrada != null ? (esYape(f) ? `(${fmt(f.entrada)})` : fmt(f.entrada)) : ''}</td>
                  <td className={`px-2 py-1.5 text-right ${f.salida != null && !esYape(f) ? 'bg-amber-100 text-red-700' : ''}`}>{f.salida != null ? (esYape(f) ? `(${fmt(f.salida)})` : fmt(f.salida)) : ''}</td>
                  <td className={`px-2 py-1.5 text-right font-medium ${f.saldo < 0 ? 'text-red-600' : ''}`}>{fmt(f.saldo)}</td>
                  <td className="px-1 text-center">
                    {f.anulable && (
                      <button onClick={() => { setAnulando(f); setClave(''); setMotivo(''); }} title="Anular esta fila" className="text-gray-400 hover:text-red-600">
                        <Ban size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-blue-100 font-bold border-t-2 border-blue-300">
                <td></td>
                <td className="px-2 py-2">TOTALES <span className="text-xs font-normal text-gray-600">(solo efectivo)</span></td>
                <td className="px-2 py-2 text-right text-green-700">{fmt(libro.total_entradas)}</td>
                <td className="px-2 py-2 text-right text-red-700">{fmt(libro.total_salidas)}</td>
                <td className="px-2 py-2 text-right">{fmt(libro.saldo_final)}</td>
                <td></td>
              </tr>
              {!!(libro.total_yape_entradas || libro.total_yape_salidas) && (
                <tr className="bg-purple-50 text-purple-900 text-sm">
                  <td></td>
                  <td className="px-2 py-1.5">Cobrado por Yape en estas fechas <span className="text-xs">(no está en la caja)</span></td>
                  <td className="px-2 py-1.5 text-right">{fmt(libro.total_yape_entradas || 0)}</td>
                  <td className="px-2 py-1.5 text-right">{libro.total_yape_salidas ? fmt(libro.total_yape_salidas) : ''}</td>
                  <td></td><td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">
        Con "Agregar fila" se anotan ventas (descuentan stock), gastos y compras de repuestos; el botón <Ban size={11} className="inline" /> anula una fila, también un pago de deuda mal registrado (pide clave y queda en Auditoría).
        Los pagos de deuda se registran en Créditos. La parte de servicio (mano de obra) de un cobro entra directo a Caja Servicios, por eso no aparece acá.
        El saldo es solo el <b>efectivo</b>: lo cobrado por Yape se ve en gris entre paréntesis y no suma. Toca <b>PRECIO CAMBIADO</b> para ver por qué se cambió el precio.
      </p>

      {verPrecio?.precio_cambiado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3"
          {...cerrarAlTocarFondo(() => setVerPrecio(null))}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-bold text-orange-700 flex items-center gap-1"><Tag size={18} /> Precio cambiado</h3>
            <div className="text-sm text-gray-600">{fechaCorta(verPrecio.fecha)} · {verPrecio.descripcion}</div>
            <table className="w-full text-sm border">
              <thead><tr className="bg-orange-50 text-xs"><th className="px-2 py-1 text-left">Producto</th><th className="px-2 py-1 text-right">Lista</th><th className="px-2 py-1 text-right">Cobrado</th></tr></thead>
              <tbody>
                {verPrecio.precio_cambiado.cambios.map((c, k) => (
                  <tr key={k} className="border-t">
                    <td className="px-2 py-1">{c.cantidad} {c.producto}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{fmt(Number(c.precio_lista) || 0)}</td>
                    <td className="px-2 py-1 text-right font-semibold">{fmt(Number(c.precio_cobrado) || 0)}</td>
                  </tr>
                ))}
                {verPrecio.precio_cambiado.descuento > 0 && (
                  <tr className="border-t"><td className="px-2 py-1">Descuento</td><td></td><td className="px-2 py-1 text-right font-semibold">-{fmt(verPrecio.precio_cambiado.descuento)}</td></tr>
                )}
              </tbody>
            </table>
            <div className="text-sm bg-orange-50 border border-orange-200 rounded p-2">
              <div className="text-xs text-gray-500">Motivo</div>
              <div className="font-semibold">{verPrecio.precio_cambiado.motivo || '(sin motivo)'}</div>
            </div>
            {verPrecio.precio_cambiado.usuario && <div className="text-xs text-gray-500">Lo cambió: {verPrecio.precio_cambiado.usuario}</div>}
            <button onClick={() => setVerPrecio(null)} className="w-full py-2 bg-gray-200 rounded-lg">Cerrar</button>
          </div>
        </div>
      )}

      {mostrarAgregar && <FilaLibroModal onClose={() => setMostrarAgregar(false)} onGuardado={refrescar} />}

      {anulando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="text-lg font-bold text-red-700">Anular fila</h3>
            <div className="text-sm bg-gray-50 border rounded p-2">
              <div>{fechaCorta(anulando.fecha)} · {anulando.descripcion}</div>
              <div className="font-semibold">{anulando.entrada != null ? `Entrada S/ ${fmt(anulando.entrada)}` : `Salida S/ ${fmt(anulando.salida || 0)}`}</div>
            </div>
            <p className="text-xs text-gray-600">
              {anulando.origen === 'venta' ? 'Se anula la venta completa: vuelve el stock y sale la plata de la caja.'
                : anulando.origen === 'compra' ? 'Se anula la compra: la plata vuelve a la caja y, si se había sumado al stock, se resta.'
                : anulando.origen === 'pago_deuda_antigua' || anulando.origen === 'pago_deuda' ? 'Se anula el pago de deuda: la plata sale de la caja y la deuda del cliente vuelve a subir. Si el cliente pagó otro monto, regístralo después en Créditos.'
                : 'Se anula el gasto: la plata vuelve a la caja.'} Queda registrado en Auditoría.
            </p>
            <input type="password" value={clave} onChange={e => setClave(e.target.value)} placeholder="Clave de administrador" className="w-full px-3 py-2 border rounded-lg" />
            <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo (ej: se anotó dos veces)" className="w-full px-3 py-2 border rounded-lg" />
            <div className="flex gap-2">
              <button onClick={() => setAnulando(null)} disabled={procesando} className="flex-1 py-2 bg-gray-200 rounded-lg">Cancelar</button>
              <button onClick={confirmarAnular} disabled={procesando} className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold disabled:opacity-60">
                {procesando ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibroCaja;
